import type { ReactNode } from 'react';
import { cx } from '../cx';

export type SplitAsideWidth = 'rail' | 'toc' | 'side' | 'wide';

export type SplitLayoutProps = {
  children: ReactNode;
  /** Nothing renders for the column when this is empty (a page with no contents or side card). */
  aside?: ReactNode;
  /** Left (a brand rail, side navigation, contents) or right (a rail of related things). */
  asidePosition?: 'left' | 'right';
  /** rail 210px (job page brand rail), toc 224px, side 320px, wide 384px. */
  asideWidth?: SplitAsideWidth;
  /** Keep the aside in view while the main column scrolls (from lg). */
  stickyAside?: boolean;
  /** On phones the aside comes first (a brand rail as a top block) or after the main column. */
  asideFirstOnPhones?: boolean;
  className?: string;
};

const WIDTH: Record<SplitAsideWidth, string> = { rail: 'lg:w-52.5', toc: 'lg:w-56', side: 'lg:w-80', wide: 'lg:w-96' };

/** A main column with a side column from lg; one column on phones and tablets. */
export function SplitLayout({
  children,
  aside,
  asidePosition = 'right',
  asideWidth = 'side',
  stickyAside = false,
  asideFirstOnPhones = false,
  className,
}: SplitLayoutProps) {
  const side = (
    <aside
      className={cx(
        'min-w-0 shrink-0',
        WIDTH[asideWidth],
        stickyAside && 'lg:sticky lg:top-24 lg:self-start',
        asidePosition === 'left' ? 'lg:order-first' : 'lg:order-last',
        asideFirstOnPhones ? 'order-first' : 'order-last',
      )}
    >
      {aside}
    </aside>
  );
  return (
    <div className={cx('flex flex-col gap-jc-block lg:flex-row lg:gap-jc-section', className)}>
      <div className="min-w-0 flex-1">{children}</div>
      {aside ? side : null}
    </div>
  );
}
